import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useWebSocket } from '@/lib/websocket';
import { LayoutDashboard, Package, PackagePlus, ScanLine, Map, Users, FileText, Settings, Truck, ClipboardList, LogOut, Menu, X, Tag, RotateCcw, ClipboardCheck, Wifi, Wrench } from 'lucide-react';
import { BRAND_BLUE, NAVY } from '@/lib/constants';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';

const LOGO_URL = '/sturdfix-logo.jpg';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/inventory', icon: Package, label: 'Inventory' },
  { path: '/pack', icon: PackagePlus, label: 'Pack' },
  { path: '/scan', icon: ScanLine, label: 'Scan' },
  { path: '/map', icon: Map, label: 'Map' },
];

const moreItems = [
  { path: '/customers', icon: Users, label: 'Customers' },
  { path: '/grn', icon: Truck, label: 'GRN' },
  { path: '/labels', icon: Tag, label: 'Labels' },
  { path: '/cycle-count', icon: ClipboardCheck, label: 'Cycle Count' },
  { path: '/returns', icon: RotateCcw, label: 'Returns' },
  { path: '/tool-store', icon: Wrench, label: 'Tool Store', roles: ['admin', 'manager'] },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/admin', icon: Settings, label: 'Admin', roles: ['admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { isConnected } = useWebSocket();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const roleLabel = { admin: 'Admin', manager: 'Manager', user: 'Staff' };

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      {/* Desktop Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-4 shadow-sm transition-all" style={{ background: `linear-gradient(135deg, ${NAVY}, ${BRAND_BLUE})` }}>
        <button className="lg:hidden mr-3 text-white" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="menu-toggle">
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <img src={LOGO_URL} alt="Sturdfix" className="h-8 w-auto mr-2 rounded" />
        <span className="text-white font-bold text-lg hidden sm:inline">Sturdfix</span>
        <span className="text-[#e8191a] font-bold text-lg ml-1 hidden sm:inline">WMS</span>
        
        {/* Connection status indicator - only show when connected */}
        <div className="ml-3 hidden sm:flex items-center gap-1">
          {isConnected && (
            <Wifi size={14} className="text-green-400" title="Live sync active" />
          )}
        </div>
        
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-white text-sm font-medium">{user?.full_name}</div>
            <div className="text-white/70 text-xs">{roleLabel[user?.role] || user?.role}</div>
          </div>
          <button onClick={logout} className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10" data-testid="logout-btn">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className={`fixed top-14 left-0 bottom-0 w-56 bg-white shadow-lg z-40 transition-all duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="flex flex-col py-3 h-full">
          <div className="flex-1 space-y-0.5 px-2">
            {[...navItems, ...moreItems].map((item) => {
              if (item.roles && !item.roles.includes(user?.role)) return null;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.path) ? 'bg-[#1a2fce]/10 text-[#1a2fce]' : 'text-[#5f6f82] hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  <item.icon size={18} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="pt-14 lg:pl-56 min-h-screen pb-20 lg:pb-4 transition-all">
        <div className="p-4 max-w-7xl mx-auto animate-fadeIn">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 lg:hidden no-print">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-lg transition-all min-w-[56px] ${
                isActive(item.path) ? 'text-[#1a2fce]' : 'text-[#5f6f82]'
              }`}
            >
              <item.icon size={20} strokeWidth={isActive(item.path) ? 2.5 : 1.8} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
}
